import { Injectable } from '@angular/core';
import timezones from 'timezones-list';

/**
 * Service responsible for managing timezone-related operations.
 * Follows Single Responsibility Principle by focusing solely on timezone management.
 */
@Injectable({
  providedIn: 'root'
})
export class TimezoneService {
  private timezonesCache: string[] | null = null;

  /**
   * Gets a sorted list of all available timezone labels.
   * Results are cached for performance.
   * 
   * @returns Array of timezone labels sorted alphabetically
   */
  getTimeZones(): string[] {
    if (this.timezonesCache === null) {
      this.timezonesCache = timezones.map(tz => tz.label).sort();
    }
    return this.timezonesCache;
  }

  /**
   * Gets the browser's current timezone.
   * 
   * @returns IANA timezone identifier (e.g., "America/New_York") or null if unavailable
   */
  getBrowserTimezone(): string | null {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return null;
    }
  }

  /**
   * Finds a timezone label that starts with the given IANA timezone identifier.
   * 
   * @param ianaTimezone - IANA timezone identifier (e.g., "America/New_York")
   * @returns Matching timezone label or null if not found
   */
  findTimezoneByIana(ianaTimezone: string): string | null {
    if (!ianaTimezone) {
      return null;
    }

    const timezones = this.getTimeZones();
    return timezones.find(tz => tz.startsWith(ianaTimezone)) || null;
  }

  /**
   * Gets the default timezone for the current browser environment.
   * Attempts to match browser timezone, falls back to UTC if not found.
   * 
   * @returns Timezone label or null if no match found
   */
  getDefaultTimezone(): string | null {
    const browserTz = this.getBrowserTimezone();
    
    if (browserTz) {
      const match = this.findTimezoneByIana(browserTz);
      if (match) {
        return match;
      }
    }

    // Fallback to UTC label if present
    const utcLabel = this.findTimezoneByIana('UTC');
    return utcLabel;
  }
}

